class ProductBundle extends HTMLElement {
  constructor() {
    super();

    this.listItem = this.querySelectorAll('.bundle-product-item');
    this.form = this.querySelector('form');
    this.bundleDisclosureToggle = this.querySelector('[data-fbt-disclosure-toggle]');
    this.bundleDisclosureContent = this.querySelector('[data-fbt-disclosure-content]');
    this.bundleDisclosureStateKey = `cyril-fbt-expanded-v3:${this.dataset.productId || window.location.pathname}`;
    this.bundleDesktopMediaQuery = window.matchMedia('(min-width: 990px)');
    this.bundleSourceSection = this.closest('[data-cyril-fbt-source-section]');
    this.bundleSourceBackground = this.bundleSourceSection?.previousElementSibling || null;
    this.bundleOriginalParent = this.parentNode;
    this.bundleOriginalNextSibling = this.nextSibling;

    this.initializeBundlePlacement();
    this.initializeBundleDisclosure();

    this.updateBundleTotalPrice();
    this.updateBundleText();

    this.form.addEventListener('change', this.onVariantChange.bind(this));
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

    if (this.querySelector('.card--block-title')) {
      this.querySelectorAll('.card--block-title').forEach((checkbox) => {
        checkbox.addEventListener('click', this.onHandleCheckedProduct.bind(this));
      });

      document.body.addEventListener('click', this.onBodyClickEvent.bind(this));
    }

    if (this.querySelector('.bundle-product-toggle')) {
      this.querySelectorAll('.bundle-product-toggle').forEach((button) => {
        button.addEventListener('click', this.onHandleToggleProduct.bind(this));
      });

      document.body.addEventListener('click', this.onBodyClickEvent.bind(this));
    }

    if (this.querySelector('.bundle-option-close')) {
      this.querySelectorAll('.bundle-option-close').forEach((button) => {
        button.addEventListener('click', this.onHandleCloseProduct.bind(this));
      });
    }
  }

  initializeBundlePlacement() {
    this.handleBundlePlacementChange = this.updateBundlePlacement.bind(this);

    this.handleBundlePlacementChange();

    if (this.bundleDesktopMediaQuery.addEventListener) {
      this.bundleDesktopMediaQuery.addEventListener('change', this.handleBundlePlacementChange);
    } else {
      this.bundleDesktopMediaQuery.addListener(this.handleBundlePlacementChange);
    }
  }

  updateBundlePlacement() {
    const desktopSlot = document.querySelector('.main-product-information [data-cyril-fbt-desktop-slot]');
    const shouldUseDesktopSlot = this.bundleDesktopMediaQuery.matches && desktopSlot;

    if (shouldUseDesktopSlot) {
      if (!desktopSlot.contains(this)) desktopSlot.appendChild(this);
      this.bundleSourceSection?.classList.add('is-cyril-fbt-relocated');
      this.bundleSourceBackground?.classList.add('is-cyril-fbt-relocated');
      this.bundleSourceSection?.removeAttribute('data-cyril-fbt-placement-pending');
      return;
    }

    if (this.bundleOriginalParent && this.parentNode !== this.bundleOriginalParent) {
      const referenceNode = this.bundleOriginalNextSibling?.parentNode === this.bundleOriginalParent
        ? this.bundleOriginalNextSibling
        : null;
      this.bundleOriginalParent.insertBefore(this, referenceNode);
    }

    this.bundleSourceSection?.classList.remove('is-cyril-fbt-relocated');
    this.bundleSourceBackground?.classList.remove('is-cyril-fbt-relocated');
    this.bundleSourceSection?.removeAttribute('data-cyril-fbt-placement-pending');
  }

  initializeBundleDisclosure() {
    if (!this.bundleDisclosureToggle || !this.bundleDisclosureContent) return;

    /*
     * The bundle starts collapsed everywhere, phones included.
     *
     * It used to default to expanded on mobile and only wrote a preference once
     * the shopper clicked the toggle, so the session storage key described
     * whether the shopper had interacted rather than what they preferred. Any
     * stored value is ignored here so every visit starts from the compact
     * header, which keeps the product page from opening on a wall of options.
     * The key was also bumped to v3 so a value written by the previous version
     * cannot open the panel again.
     *
     * A shopper who expands the bundle keeps it expanded while they stay on the
     * page, and the choice is not carried into the next product.
     */
    this.setBundleDisclosureState(false);

    this.bundleDisclosureToggle.addEventListener('click', () => {
      const nextExpanded = this.bundleDisclosureToggle.getAttribute('aria-expanded') !== 'true';
      this.setBundleDisclosureState(nextExpanded);

      try {
        window.sessionStorage.setItem(this.bundleDisclosureStateKey, String(nextExpanded));
      } catch (error) {
        // Session storage can be unavailable in private browsing contexts.
      }
    });
  }

  setBundleDisclosureState(expanded) {
    this.bundleDisclosureToggle.setAttribute('aria-expanded', String(expanded));
    this.bundleDisclosureToggle.setAttribute(
      'aria-label',
      `${expanded ? 'Collapse' : 'Expand'} Frequently bought together`
    );
    this.bundleDisclosureContent.hidden = !expanded;
    this.classList.toggle('is-collapsed', !expanded);

    const disclosureText = this.bundleDisclosureToggle.querySelector('[data-fbt-disclosure-text]');
    if (disclosureText) disclosureText.textContent = expanded ? 'Collapse' : 'Expand';
  }

  onVariantChange(event) {
    this.changeSwatch(event);
    this.updateVariants(event);
  }

  getAvailableBundleVariant(item) {
    const variantSelect = item.querySelector('select[name="group_id"]');

    if (variantSelect) {
      let selectedOption = variantSelect.options[variantSelect.selectedIndex];

      if (!selectedOption || selectedOption.disabled) {
        selectedOption = Array.from(variantSelect.options).find((option) => !option.disabled && option.value);
        if (selectedOption) variantSelect.value = selectedOption.value;
      }

      if (!selectedOption?.value) return null;
      return { id: Number(selectedOption.value), quantity: 1 };
    }

    const variantInput = item.querySelector('input[name="group_id"]');
    if (!variantInput?.value || variantInput.disabled) return null;

    return { id: Number(variantInput.value), quantity: 1 };
  }

  showBundleError(message) {
    const errorElement = this.querySelector('[data-bundle-error]');
    if (!errorElement) return;

    errorElement.textContent = message;
    errorElement.hidden = false;
  }

  clearBundleError() {
    const errorElement = this.querySelector('[data-bundle-error]');
    if (!errorElement) return;

    errorElement.textContent = '';
    errorElement.hidden = true;
  }

  onSubmitHandler(event) {
    event.preventDefault();

    this.clearBundleError();

    const btnAddToCart = this.querySelector('[data-bundle-addtocart]');
    const bundleItems = Array.from(
      this.querySelectorAll('.bundle-product-item.isChecked[data-bundle-available="true"]')
    );
    const items = bundleItems
      .map((item) => this.getAvailableBundleVariant(item))
      .filter(Boolean);

    if (!items.length || items.length !== bundleItems.length) {
      this.showBundleError('Please choose an available option for every selected product.');
      return;
    }

    const previousValue = btnAddToCart?.value;
    const loadingText = window.variantStrings?.addingToCart || 'Adding...';

    if (btnAddToCart) {
      btnAddToCart.value = loadingText;
      btnAddToCart.disabled = true;
    }

    this.querySelector('form>div')?.classList.add('has-halo-block-loader');

    const configuredCartAddUrl = window.routes?.cart_add_url || '/cart/add';
    const cartAddUrl = configuredCartAddUrl.endsWith('.js')
      ? configuredCartAddUrl
      : `${configuredCartAddUrl}.js`;

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items })
    })
      .then((response) => {
        if (!response.ok) return response.json().then((error) => Promise.reject(error));
        return response.json();
      })
      .then((payload) => {
        if (!payload?.items?.length) throw new Error('Shopify did not return the added cart items.');
        this.redirectTo(window.routes?.cart_url || '/cart');
      })
      .catch((error) => {
        console.error('Bundle add to cart failed:', error);
        this.showBundleError(error?.description || error?.message || 'Unable to add these products to the cart.');
        this.querySelector('form>div')?.classList.remove('has-halo-block-loader');
        if (btnAddToCart) {
          btnAddToCart.value = previousValue;
          btnAddToCart.disabled = false;
        }
      });
  }

  onHandleCheckedProduct(event) {
    event.preventDefault();
    const thisElement = event.currentTarget;
    const item = event.target.closest('.bundle-product-item');
    const input = thisElement.querySelector('input[type=checkbox]');

    if (!item || input?.disabled || item.dataset.bundleAvailable === 'false') return;

    const id = item.getAttribute('data-bundle-product-item-id');

    const itemElement = this.querySelector(`[data-bundle-product-item-id="${id}"]`);

    if (!itemElement.classList.contains('isChecked')) {
      itemElement.classList.add('isChecked');
      item.classList.add('isChecked');
      input.checked = true;
      input.setAttribute('checked', 'checked');
    } else {
      itemElement.classList.remove('isChecked');
      item.classList.remove('isChecked');
      input.checked = false;
      input.removeAttribute('checked');
    }

    this.updateBundleTotalPrice();
  }

  onHandleToggleProduct(event) {
    event.preventDefault();

    this.listItem.forEach((item) => {
      if (item.contains(event.target)) {
        const toggle = item.querySelector('.bundle-product-toggle');

        if (!item.classList.contains('is-open')) {
          item.classList.add('is-open');
          if (toggle) toggle.setAttribute('aria-expanded', 'true');
          this.updateVariants(event);
        } else {
          item.classList.remove('is-open');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
        }
      } else {
        item.classList.remove('is-open');
        const toggle = item.querySelector('.bundle-product-toggle');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  onHandleCloseProduct(event) {
    event.preventDefault();

    var item = event.currentTarget.closest('.bundle-product-item');

    item.classList.remove('is-open');
    item.querySelector('.bundle-product-toggle')?.setAttribute('aria-expanded', 'false');
  }

  updateBundleText(showBundleText = true) {
    if (showBundleText) {
      if (this.querySelector('.bundle-price')) {
        const bundlePrice = this.querySelector('.bundle-price');
        const discountRate = bundlePrice.getAttribute('data-bundle-discount-rate') * 100;
        const discountText = this.querySelector('.bundle-product-text');

        discountText.innerHTML = discountText.textContent.replace('[discount]', discountRate);
        discountText.style.display = 'block';
      }
    }
  }

  updateBundleTotalPrice(showBundleTotalPrice = true) {
    if (showBundleTotalPrice) {
      const bundleItem = this.querySelectorAll('.bundle-product-item.isChecked');
      const maxProduct = this.listItem.length;

      var totalPrice = 0;

      if (this.querySelector('.bundle-price')) {
        const bundlePrice = this.querySelector('.bundle-price');
        const oldPrice = this.querySelector('.bundle-old-price');
        const discountRate = bundlePrice.getAttribute('data-bundle-discount-rate');
      }

      if (bundleItem.length > 0) {
        bundleItem.forEach((item) => {
          const selectElement = item.querySelector('select[name=group_id]'),
            inputElement = item.querySelector('input[name=group_id]');

          if (selectElement) {
            var price = selectElement[selectElement.selectedIndex].getAttribute('data-price');
          } else {
            if (inputElement) {
              var price = inputElement.getAttribute('data-price');
            } else {
              var price = item.querySelector('input[name=id]').getAttribute('data-price');
            }
          }

          if (price) {
            totalPrice = totalPrice + parseFloat(price);

            if (this.querySelector('.bundle-price')) {
              const bundlePrice = this.querySelector('.bundle-price');
              const oldPrice = this.querySelector('.bundle-old-price');
              const discountRate = bundlePrice.getAttribute('data-bundle-discount-rate');

              if (bundlePrice && oldPrice) {
                oldPrice.innerHTML = Shopify.formatMoney(totalPrice, window.money_format);
                bundlePrice.innerHTML = Shopify.formatMoney(totalPrice * (1 - discountRate), window.money_format);

                if (bundleItem.length == maxProduct) {
                  bundlePrice.style.display = 'inline-block';
                  oldPrice.style.display = 'inline-block';
                  this.querySelector('[data-bundle-product-total]').style.display = 'none';
                } else {
                  bundlePrice.style.display = 'none';
                  oldPrice.style.display = 'none';
                  this.querySelector('[data-bundle-product-total]').style.display = 'block';
                }
              }
            }
            this.querySelector('[data-bundle-product-total]').innerHTML = Shopify.formatMoney(totalPrice, window.money_format);
          }
        });
      } else {
        this.querySelector('[data-bundle-product-total]').innerHTML = Shopify.formatMoney(totalPrice, window.money_format);
      }

      const addButton = this.querySelector('[data-bundle-addtocart]');
      if (addButton) {
        addButton.disabled = bundleItem.length === 0;

        if (bundleItem.length === maxProduct && maxProduct > 0) {
          addButton.value = window.total_btn.add_all_item;
        } else if (bundleItem.length > 1) {
          addButton.value = window.total_btn.add_items.replace('[item]', bundleItem.length);
        } else {
          addButton.value = window.total_btn.add_item.replace('[item]', bundleItem.length);
        }
      }
    }
  }

  updateVariants(event) {
    const item = event.target.closest('.bundle-product-item');
    const productId = item.getAttribute('data-bundle-product-item-id');
    const variants = window.productVariants[productId];
    const fieldsets = event.target.closest('.bundle-product-item').querySelectorAll('.variant-option');

    let selectedOption1;
    let selectedOption2;
    let selectedOption3;

    if (variants) {
      if (fieldsets[0]) selectedOption1 = Array.from(fieldsets[0].querySelectorAll('input')).find((radio) => radio.checked).value;
      if (fieldsets[1]) selectedOption2 = Array.from(fieldsets[1].querySelectorAll('input')).find((radio) => radio.checked).value;
      if (fieldsets[2]) selectedOption3 = Array.from(fieldsets[2].querySelectorAll('input')).find((radio) => radio.checked).value;

      var checkStatus = (optionSoldout, optionUnavailable, swatch) => {
        if (optionSoldout == undefined) {
          if (optionUnavailable == undefined) {
            swatch.classList.add('soldout');
            swatch.setAttribute('disabled', true);
          } else {
            swatch.classList.add('soldout');
            swatch.setAttribute('disabled', true);
          }
        } else {
          swatch.classList.remove('soldout');
          swatch.removeAttribute('disabled');
        }
      };

      var renderSwatch = (optionIndex, element) => {
        const swatchs = element.querySelectorAll('input[type=radio]');

        swatchs.forEach((swatch) => {
          const swatchVal = swatch.getAttribute('value');

          const optionSoldout = variants.find((variant) => {
            switch (optionIndex) {
              case 0: return variant.option1 == swatchVal && variant.available;
              case 1: return variant.option1 == selectedOption1 && variant.option2 == swatchVal && variant.available;
              case 2: return variant.option1 == selectedOption1 && variant.option2 == selectedOption2 && variant.option3 == swatchVal && variant.available;
            }
          });

          const optionUnavailable = variants.find((variant) => {
            switch (optionIndex) {
              case 0: return variant.option1 == swatchVal;
              case 1: return variant.option1 == selectedOption1 && variant.option2 == swatchVal;
              case 2: return variant.option1 == selectedOption1 && variant.option2 == selectedOption2 && variant.option3 == swatchVal;
            }
          });

          checkStatus(optionSoldout, optionUnavailable, swatch);
        });
      };

      fieldsets.forEach((element) => {
        const optionIndex = parseInt(element.getAttribute('data-option-idx'));

        renderSwatch(optionIndex, element);
      });
    }
  }

  changeSwatch(event) {
    const item = event.target.closest('.bundle-product-item');
    const fieldsets = Array.from(item.querySelectorAll('.variant-option'));
    const productId = item.getAttribute('data-bundle-product-item-id');
    const variantList = window.productVariants[productId];
    const optionIndex = parseInt(event.target.closest('[data-option-idx]').getAttribute('data-option-idx'));
    const swatches = Array.from(item.querySelectorAll('.swatch-element'));
    const thisValue = event.target.value;
    const productPrice = item.querySelector('.card--bundle .price');
    const priceSale = productPrice.querySelector('.price-sale');
    const priceOld = productPrice.querySelector('.old-price');
    const priceRegular = productPrice.querySelector('.price-item');
    const productInput = item.querySelector('[name=group_id]');
    const hotStock = item.querySelector('.bundle-hotStock');

    let selectedVariant;
    let selectedSwatchOption1;
    let selectedSwatchOption2;
    let selectedSwatchOption3;

    if (fieldsets[0]) selectedSwatchOption1 = Array.from(fieldsets[0].querySelectorAll('input')).find((radio) => radio.checked).value;
    if (fieldsets[1]) selectedSwatchOption2 = Array.from(fieldsets[1].querySelectorAll('input')).find((radio) => radio.checked).value;
    if (fieldsets[2]) selectedSwatchOption3 = Array.from(fieldsets[2].querySelectorAll('input')).find((radio) => radio.checked).value;

    swatches.forEach((swatche) => {
      swatche.classList.remove('soldout');
      const input = swatche.querySelector('input');
      input.disabled = false;
      input.removeAttribute('disabled');
    });

    switch (optionIndex) {
      case 0:
        var availableVariants = variantList.find((variant) => {
          return variant.option1 == thisValue && variant.option2 == selectedSwatchOption2 && variant.available;
        });

        if (availableVariants != undefined) {
          selectedVariant = availableVariants;
        } else {
          var altAvailableVariants = variantList.find((variant) => {
            return variant.option1 == thisValue && variant.available;
          });

          selectedVariant = altAvailableVariants;
        }

        break;
      case 1:
        var availableVariants = variantList.find((variant) => {
          return variant.option1 == selectedSwatchOption1 && variant.option2 == thisValue && variant.available;
        });

        if (availableVariants != undefined) {
          selectedVariant = availableVariants;
        } else {
          // The selected combination is sold out; selectVariant* stays empty so
          // the caller keeps the previous variant instead of switching.
        }

        break;
      case 2:
        var availableVariants = variantList.find((variant) => {
          return variant.option1 == selectedSwatchOption1 && variant.option2 == selectedSwatchOption2 && variant.option3 == thisValue && variant.available;
        });

        if (availableVariants != undefined) {
          selectedVariant = availableVariants;
        } else {
          // The selected combination is sold out; see the note above.
        }

        break;
    }

    if (!selectedVariant) return;

    productInput.value = selectedVariant.id;
    const optionToggleText = item.querySelector('.cyril-fbt-card__options-toggle span:first-child');
    if (optionToggleText) optionToggleText.textContent = selectedVariant.title;

    priceRegular.innerHTML = Shopify.formatMoney(selectedVariant.price, window.money_format);
    if (selectedVariant.compare_at_price > selectedVariant.price) {
      priceRegular.classList.add('special-price');

      if (priceOld) {
        priceOld.innerHtml = Shopify.formatMoney(selectedVariant.compare_at_price, window.money_format);
        priceOld.style.display = 'inline-block';
      }
    } else {
      if (priceOld) {
        priceOld.style.display = 'none';
      }

      priceRegular.classList.remove('special-price');
    }

    item.querySelector('select').value = selectedVariant.id;

    this.updateBundleTotalPrice();

    if (selectedVariant.inventory_management != null) {
      if (hotStock) {
        var arrayInVarName = 'bundle_inven_array_' + productId,
          inven_array = window[arrayInVarName],
          maxStock = parseInt(hotStock.getAttribute('data-bundle-hot-stock'));

        if (inven_array != undefined) {
          var inven_num = inven_array[selectedVariant.id],
            inventoryQuantity = parseInt(inven_num);
        }

        if (inventoryQuantity > 0 && inventoryQuantity <= maxStock) {
          var textStock = window.inventory_text.hotStock.replace('[inventory]', inventoryQuantity);

          hotStock.innerText = textStock;
          hotStock.style.display = 'block';
        } else {
          hotStock.style.display = 'none';
        }
      }
    }

    if (selectedVariant.featured_image) {
      const productImage = this.querySelector(`[data-bundle-product-item-id="${productId}"] img`);

      productImage.setAttribute('src', selectedVariant.featured_image.src);
      productImage.setAttribute('srcset', selectedVariant.featured_image.src);
    }
  }

  onBodyClickEvent(event) {
    if (this.querySelector('.bundle-product-item.is-open')) {
      this.listItem.forEach((item) => {
        if (!item.contains(event.target)) {
          item.classList.remove('is-open');
          item.querySelector('.bundle-product-toggle')?.setAttribute('aria-expanded', 'false');
        }
      });
    }
  }

  isRunningInIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  redirectTo(url) {
    if (this.isRunningInIframe() && !window.iframeSdk && !Shopify.designMode) {
      window.top.location = url;
    } else {
      window.location = url;
    }
  }
}

customElements.define('product-bundle', ProductBundle);
